import { dateLib } from "@agentcall/core/lib"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { uploadFiles } from "@/api/client/file"
import { type OwnerMemoList200OwnerMemosItem, ownerMemoApi } from "@/api/client/owner"
import { getErrorMessage } from "@/api/instance"
import type { OwnerMemoFormData } from "@/components/properties/OwnerMemoForm"
import { type OwnerMemoItem, OwnerMemoSection } from "./OwnerMemoSection"

const MEMO_PAGE_LIMIT = 3

function convertApiMemoToOwnerMemoItem(memo: OwnerMemoList200OwnerMemosItem): OwnerMemoItem {
  return {
    id: String(memo.id),
    date: dateLib.formatDateTime(memo.createdAt),
    title: memo.title,
    description: memo.body,
    images: memo.files?.map((file) => file.url) ?? [],
    // 内部で使用するファイル情報を保持
    _fileInfos: memo.files?.map((file) => ({ id: file.id, url: file.url })),
  }
}

interface OwnerMemoSectionContentProps {
  propertyId: number
  roomId?: number
  showAddModal?: boolean
  onShowAddModalChange?: (show: boolean) => void
}

export function OwnerMemoSectionContent({
  propertyId,
  roomId,
  showAddModal,
  onShowAddModalChange,
}: OwnerMemoSectionContentProps) {
  const [memoPage, setMemoPage] = useState(1)
  const [accumulatedMemos, setAccumulatedMemos] = useState<OwnerMemoItem[]>([])
  const [totalMemos, setTotalMemos] = useState(0)
  const [isUploading, setIsUploading] = useState(false)

  // 初回読み込み用（Suspense対応）
  const { data: initialMemoResponse, refetch: refetchInitial } =
    ownerMemoApi.useOwnerMemoListSuspense({
      propertyId,
      roomId,
      page: 1,
      limit: MEMO_PAGE_LIMIT,
      withFiles: "true",
    })

  // 追加読み込み用（non-Suspense）
  const { data: additionalMemoResponse, isFetching: isLoadingMore } = ownerMemoApi.useOwnerMemoList(
    {
      propertyId,
      roomId,
      page: memoPage,
      limit: MEMO_PAGE_LIMIT,
      withFiles: "true",
    },
    {
      query: {
        enabled: memoPage > 1,
      },
    }
  )

  // 初回データをセット
  useEffect(() => {
    if (initialMemoResponse?.ownerMemos) {
      const newMemos = initialMemoResponse.ownerMemos.map(convertApiMemoToOwnerMemoItem)
      setAccumulatedMemos(newMemos)
      setTotalMemos(initialMemoResponse.total)
    }
  }, [initialMemoResponse])

  // 追加ページのデータをマージ
  useEffect(() => {
    if (memoPage > 1 && additionalMemoResponse?.ownerMemos) {
      const newMemos = additionalMemoResponse.ownerMemos.map(convertApiMemoToOwnerMemoItem)
      setAccumulatedMemos((prev) => {
        const existingIds = new Set(prev.map((m) => m.id))
        const uniqueNewMemos = newMemos.filter((m) => !existingIds.has(m.id))
        return [...prev, ...uniqueNewMemos]
      })
      setTotalMemos(additionalMemoResponse.total)
    }
  }, [additionalMemoResponse, memoPage])

  const ownerMemos = accumulatedMemos
  const hasMoreMemos = totalMemos > accumulatedMemos.length

  // 表示中のメモが空になったが、まだサーバー側にメモがある場合はリフレッシュ
  useEffect(() => {
    if (accumulatedMemos.length === 0 && totalMemos > 0) {
      setMemoPage(1)
      refetchInitial()
    }
  }, [accumulatedMemos.length, totalMemos, refetchInitial])

  const refreshMemos = async () => {
    setMemoPage(1)
    setAccumulatedMemos([])
    await refetchInitial()
  }

  const { mutateAsync: createMemo, isPending: isCreating } = ownerMemoApi.useOwnerMemoCreate({
    mutation: {
      onSuccess: () => {
        // 新規作成時は先頭にメモが追加されるため、全体をリフレッシュ
        refreshMemos()
        toast.success("メモを作成しました")
      },
      onError: (error) => {
        toast.error("メモの作成に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const { mutateAsync: updateMemo, isPending: isUpdating } = ownerMemoApi.useOwnerMemoUpdate({
    mutation: {
      onSuccess: () => {
        // 更新後にファイル情報を含む最新データを取得
        refreshMemos()
        toast.success("メモを更新しました")
      },
      onError: (error) => {
        toast.error("メモの更新に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const { mutateAsync: deleteMemo, isPending: isDeleting } = ownerMemoApi.useOwnerMemoDelete({
    mutation: {
      onSuccess: () => {
        // 削除時はローカル状態を更新するため、ここでは何もしない
        toast.success("メモを削除しました")
      },
      onError: (error) => {
        toast.error("メモの削除に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleCreateMemo = async (data: OwnerMemoFormData): Promise<OwnerMemoItem | undefined> => {
    // 新規ファイルがあればS3にアップロード
    let uploadedFiles: { path: string; fileName: string }[] = []
    if (data.newFiles && data.newFiles.length > 0) {
      setIsUploading(true)
      try {
        const uploadResults = await uploadFiles(data.newFiles)
        uploadedFiles = uploadResults.map((result) => ({
          path: result.key,
          fileName: result.fileName,
        }))
      } finally {
        setIsUploading(false)
      }
    }

    const result = await createMemo({
      data: {
        propertyId,
        roomId: roomId ?? null,
        title: data.title,
        body: data.description,
        uploadFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
      },
    })
    // 作成後は refreshMemos() が呼ばれるため、ここでは基本情報のみ返す
    // 画像は refreshMemos() 後のデータで更新される
    return {
      id: String(result.id),
      date: dateLib.formatDateTime(new Date()),
      title: data.title,
      description: data.description,
      images: data.images, // 一時的に表示用（refreshMemos後に正しいURLに更新される）
    }
  }

  const handleUpdateMemo = async (
    memoId: string,
    data: OwnerMemoFormData
  ): Promise<OwnerMemoItem | undefined> => {
    // 新規ファイルがあればS3にアップロード
    let uploadedFiles: { path: string; fileName: string }[] = []
    if (data.newFiles && data.newFiles.length > 0) {
      setIsUploading(true)
      try {
        const uploadResults = await uploadFiles(data.newFiles)
        uploadedFiles = uploadResults.map((result) => ({
          path: result.key,
          fileName: result.fileName,
        }))
      } finally {
        setIsUploading(false)
      }
    }

    await updateMemo({
      data: {
        title: data.title,
        body: data.description,
        uploadFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        deleteFileIds:
          data.deleteFileIds && data.deleteFileIds.length > 0 ? data.deleteFileIds : undefined,
      },
      id: Number(memoId),
    })
    // 更新後は onSuccess で refreshMemos() が呼ばれるため、一時的な表示用データを返す
    return {
      id: memoId,
      date: dateLib.formatDateTime(new Date()),
      title: data.title,
      description: data.description,
      images: data.images, // 一時的に表示用（refreshMemos後に正しいURLに更新される）
    }
  }

  const handleDeleteMemo = async (memoId: string): Promise<boolean> => {
    await deleteMemo({ id: Number(memoId) })
    // ローカル状態から削除（ページリセットなし）
    setAccumulatedMemos((prev) => prev.filter((memo) => memo.id !== memoId))
    setTotalMemos((prev) => prev - 1)
    return true
  }

  const handleLoadMoreMemos = () => {
    if (hasMoreMemos && !isLoadingMore) {
      setMemoPage((prev) => prev + 1)
    }
  }

  return (
    <OwnerMemoSection
      memos={ownerMemos}
      onCreateMemo={handleCreateMemo}
      onUpdateMemo={handleUpdateMemo}
      onDeleteMemo={handleDeleteMemo}
      onLoadMore={hasMoreMemos ? handleLoadMoreMemos : undefined}
      showAddModal={showAddModal}
      onShowAddModalChange={onShowAddModalChange}
      hideHeader
      isCreating={isCreating || isUploading}
      isUpdating={isUpdating || isUploading}
      isDeleting={isDeleting}
      isLoadingMore={isLoadingMore}
    />
  )
}
